<template>
  <main class="settings-page">
    <header class="settings-page__header">
      <div>
        <p class="settings-page__eyebrow">Account</p>
        <h1>Settings</h1>
        <p class="settings-page__subtitle">Manage your profile and synced interface preferences.</p>
      </div>
      <Button variant="soft" tone="neutral" size="sm" :disabled="isRefreshingProfile" @click="refreshProfile(true)">
        <template #leading>
          <i class="material-icons" aria-hidden="true">sync</i>
        </template>
        Refresh profile
      </Button>
    </header>

    <div v-if="statusMessage" class="settings-status" :class="`settings-status--${statusTone}`">
      {{ statusMessage }}
    </div>

    <section class="settings-page__grid">
      <article class="settings-panel settings-profile-panel">
        <div class="settings-panel__header">
          <div>
            <h2>Profile</h2>
            <p>Your current account information.</p>
          </div>
        </div>

        <div class="settings-profile-card">
          <img :src="profilePhoto" alt="User avatar" class="settings-profile-card__avatar" />
          <div>
            <strong>{{ currentUser?.displayName || 'User' }}</strong>
            <span>{{ currentUser?.email || 'Email unavailable' }}</span>
          </div>
        </div>
      </article>

      <article class="settings-panel settings-preferences-panel">
        <div class="settings-panel__header">
          <div>
            <h2>Preferences</h2>
            <p>Interface options synced across your devices.</p>
          </div>
        </div>

        <label class="settings-field">
          <span>Theme</span>
          <Select v-model="themeSelection" :options="themeOptions" variant="form" aria-label="Theme preference" />
        </label>

        <label class="settings-field">
          <span>Default list length</span>
          <Select v-model="defaultListLengthSelection" :options="listLengthOptions" variant="form"
            aria-label="Default shipment list length" :disabled="isSavingListLength" />
        </label>
      </article>

      <article id="notifications" class="settings-panel settings-notifications-panel">
        <div class="settings-panel__header settings-panel__header--split">
          <div>
            <h2>Notifications</h2>
            <p>Choose which browser and PWA alerts you receive. Slack DMs are always sent for direct @mentions.</p>
          </div>
          <Button v-if="!pushEnabled" variant="soft" tone="primary" size="sm"
            :disabled="isChangingPush" @click="enablePush">
            Enable browser alerts
          </Button>
          <Button v-else variant="soft" tone="neutral" size="sm" :disabled="isChangingPush" @click="disablePush">
            Disable on this device
          </Button>
        </div>

        <p class="settings-notifications-panel__device">Browser alerts: <strong>{{ pushPermissionLabel }}</strong></p>
        <label v-for="option in notificationPreferenceOptions" :key="option.key" class="settings-notification-toggle">
          <span>
            <strong>{{ option.label }}</strong>
            <small>{{ option.description }}</small>
          </span>
          <input type="checkbox" :checked="notificationPreferences[option.key]" :disabled="isSavingNotifications"
            @change="toggleNotificationPreference(option.key, $event)" />
        </label>
      </article>
    </section>

    <section v-if="hasApiAccess" class="settings-panel settings-api-panel">
      <div class="settings-panel__header settings-panel__header--split">
        <div>
          <h2>API keys</h2>
          <p>Use named keys for external tools such as Postman. Raw tokens are shown only once.</p>
        </div>
        <Button variant="soft" tone="primary" size="sm" :disabled="!hasApiAccess || isLoadingKeys" @click="loadApiKeys">
          <template #leading>
            <i class="material-icons" aria-hidden="true">refresh</i>
          </template>
          Reload
        </Button>
      </div>

      <div class="settings-api-tabs" role="tablist" aria-label="API key status">
        <button type="button" role="tab" :aria-selected="apiKeyView === 'active'"
          :class="{ active: apiKeyView === 'active' }" @click="apiKeyView = 'active'">
          Active <span>{{ activeApiKeys.length }}</span>
        </button>
        <button type="button" role="tab" :aria-selected="apiKeyView === 'archived'"
          :class="{ active: apiKeyView === 'archived' }" @click="apiKeyView = 'archived'">
          Archived <span>{{ archivedApiKeys.length }}</span>
        </button>
      </div>

      <div v-if="newToken" class="settings-token-reveal" role="status">
        <div>
          <span>Copy this token now. It will not be shown again.</span>
          <code>{{ newToken }}</code>
        </div>
        <Button variant="solid" tone="primary" size="sm" @click="copyToken">
          <template #leading>
            <i class="material-icons" aria-hidden="true">content_copy</i>
          </template>
          Copy
        </Button>
      </div>

      <form class="settings-api-form" @submit.prevent="createKey">
        <label class="settings-field settings-field--grow">
          <span>Key name</span>
          <Input v-model="newKeyName" variant="form" placeholder="Postman testing" maxlength="80"
            :disabled="!hasApiAccess" />
        </label>
        <label class="settings-field settings-field--compact">
          <span>Calls/min</span>
          <Input v-model="rateLimitPerMinute" type="number" variant="form" :disabled="!hasApiAccess" />
        </label>
        <label class="settings-field settings-field--compact">
          <span>Calls/day</span>
          <Input v-model="rateLimitPerDay" type="number" variant="form" :disabled="!hasApiAccess" />
        </label>
        <Button type="submit" tone="primary" :disabled="!hasApiAccess || isCreatingKey || !newKeyName.trim()">
          <template #leading>
            <i class="material-icons" aria-hidden="true">add</i>
          </template>
          Create key
        </Button>
      </form>

      <div v-if="isLoadingKeys" class="settings-empty-state">
        <span class="settings-spinner" aria-hidden="true"></span>
        Loading API keys
      </div>

      <div v-else-if="visibleApiKeys.length === 0" class="settings-empty-state">
        {{ apiKeyView === 'active' ? 'No active API keys.' : 'No archived API keys.' }}
      </div>

      <div v-else class="settings-api-table-wrap">
        <table class="settings-api-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Token ref</th>
              <th>Rate limit</th>
              <th>Last used</th>
              <th>Requests</th>
              <th v-if="apiKeyView === 'active'" class="settings-api-table__actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="key in visibleApiKeys" :key="key.keyId">
              <td>
                <strong>{{ key.name }}</strong>
                <span>{{ formatDate(key.createdAt) }}</span>
              </td>
              <td>
                <Tag :tone="key.status === 'active' ? 'success' : 'danger'" size="sm">{{ key.status }}</Tag>
              </td>
              <td><code>{{ key.tokenPrefix }}...{{ key.tokenSuffix }}</code></td>
              <td>{{ key.rateLimit.perMinute }}/min · {{ key.rateLimit.perDay }}/day</td>
              <td>{{ formatDate(key.lastUsedAt) }}</td>
              <td>{{ key.requestCount }}</td>
              <td v-if="apiKeyView === 'active'" class="settings-api-table__actions">
                <Button variant="ghost" tone="primary" size="sm"
                  :disabled="key.status !== 'active' || actionKeyId === key.keyId" @click="rotateKey(key.keyId)">
                  Rotate
                </Button>
                <Button variant="ghost" tone="danger" size="sm"
                  :disabled="key.status !== 'active' || actionKeyId === key.keyId" @click="deleteKey(key.keyId)">
                  Revoke
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { httpsCallable } from 'firebase/functions';

import {
  refreshCurrentUserProfile,
  getCurrentUser,
  updateCurrentUserPreferences,
  USER_PROFILE_UPDATED_EVENT,
  type CachedUser
} from '@/modules/firebase/Auth';
import { functions } from '@/modules/firebase';
import { getThemeMode, setThemeMode, subscribeTheme, type ThemeMode } from '@/modules/ui/theme';
import { Button, Input, Select, Tag } from '@/components/ui';
import type { SelectOption } from '@/components/ui';
import type { Unsubscribe } from 'firebase/firestore';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  saveNotificationPreferences,
  subscribeNotificationPreferences,
  type NotificationPreferences
} from '@/modules/notifications/NotificationClient';
import {
  disableNotificationPush,
  enableNotificationPush,
  getPushPermissionState,
  isNotificationPushEnabled
} from '@/modules/notifications/PushNotifications';

type ApiKeyStatus = 'active' | 'deleted';

interface ApiKeyRateLimit {
  enabled: boolean;
  perMinute: number;
  perDay: number;
  burst: number;
}

interface ApiKeyMetadata {
  keyId: string;
  ownerUid: string;
  name: string;
  tokenPrefix: string;
  tokenSuffix: string;
  status: ApiKeyStatus;
  rateLimit: ApiKeyRateLimit;
  createdAt: unknown;
  modifiedAt: unknown;
  rotatedAt?: unknown;
  deletedAt?: unknown;
  lastUsedAt?: unknown;
  lastUsedPath?: string | null;
  lastUsedMethod?: string | null;
  requestCount: number;
}

interface CallableEnvelope<T> {
  success: boolean;
  data: T;
}

const listApiKeysCallable = httpsCallable<undefined, CallableEnvelope<{ apiKeys: ApiKeyMetadata[] }>>(functions, 'listApiKeys');
const createApiKeyCallable = httpsCallable<Record<string, unknown>, CallableEnvelope<{ token: string; apiKey: ApiKeyMetadata }>>(functions, 'createApiKey');
const rotateApiKeyCallable = httpsCallable<{ keyId: string }, CallableEnvelope<{ token: string; apiKey: ApiKeyMetadata }>>(functions, 'rotateApiKey');
const deleteApiKeyCallable = httpsCallable<{ keyId: string }, CallableEnvelope<{ apiKey: ApiKeyMetadata }>>(functions, 'deleteApiKey');

const currentUser = ref<CachedUser | null>(getCurrentUser());
const isProfileReady = ref(false);
const isRefreshingProfile = ref(false);
const isLoadingKeys = ref(false);
const isCreatingKey = ref(false);
const actionKeyId = ref<string | null>(null);
const apiKeys = ref<ApiKeyMetadata[]>([]);
const apiKeyView = ref<'active' | 'archived'>('active');
const newKeyName = ref('Postman testing');
const rateLimitPerMinute = ref('60');
const rateLimitPerDay = ref('1000');
const newToken = ref('');
const statusMessage = ref('');
const statusTone = ref<'success' | 'danger' | 'neutral'>('neutral');
const themeSelection = ref<ThemeMode>(getThemeMode());
const isSavingListLength = ref(false);
const notificationPreferences = ref<NotificationPreferences>({ ...DEFAULT_NOTIFICATION_PREFERENCES });
const isSavingNotifications = ref(false);
const isChangingPush = ref(false);
const pushPermission = ref<NotificationPermission | 'unsupported'>(getPushPermissionState());
const pushEnabled = ref(isNotificationPushEnabled());

const DEFAULT_LIST_LENGTH = 10;
const VALID_LIST_LENGTHS = [10, 15, 20] as const;

const themeOptions: SelectOption[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' }
];

const listLengthOptions: SelectOption[] = VALID_LIST_LENGTHS.map(value => ({
  value: String(value),
  label: `${value} rows`
}));

const hasApiAccess = computed(() => isProfileReady.value && (
  currentUser.value?.permissions?.apiAccess === true || currentUser.value?.permissions?.adminAccess === true
));
const profilePhoto = computed(() => currentUser.value?.slack?.avatar || currentUser.value?.photoURL || '/assets/img/avatar.png');
const defaultListLengthSelection = ref(String(resolveDefaultListLength()));
const activeApiKeys = computed(() => apiKeys.value.filter(key => key.status === 'active'));
const archivedApiKeys = computed(() => apiKeys.value.filter(key => key.status === 'deleted'));
const visibleApiKeys = computed(() => apiKeyView.value === 'active' ? activeApiKeys.value : archivedApiKeys.value);
let statusTimeout: ReturnType<typeof setTimeout> | null = null;
let unsubscribeTheme: (() => void) | null = null;
let unsubscribeNotificationPreferences: Unsubscribe | null = null;

const notificationPreferenceOptions: Array<{ key: keyof NotificationPreferences; label: string; description: string }> = [
  { key: 'pushMentions', label: 'Browser mention alerts', description: 'Show a private browser or PWA alert for mentions.' },
  { key: 'pushStarredActivity', label: 'New messages on starred shipments', description: 'Show an alert when someone posts a human activity-feed message on a shipment you starred.' },
  { key: 'pushShipmentMilestones', label: 'Tracking milestones on starred shipments', description: 'Show an alert when a starred shipment is out for delivery, delivered, has an exception, or is returned.' }
];

const pushPermissionLabel = computed(() => {
  if (pushEnabled.value) return 'Enabled on this device';
  if (pushPermission.value === 'denied') return 'Blocked by browser settings';
  if (pushPermission.value === 'unsupported') return 'Not supported';
  return 'Not enabled';
});

async function toggleNotificationPreference(key: keyof NotificationPreferences, event: Event): Promise<void> {
  const checked = (event.target as HTMLInputElement).checked;
  const previous = notificationPreferences.value[key];
  notificationPreferences.value = { ...notificationPreferences.value, [key]: checked };
  isSavingNotifications.value = true;
  try {
    notificationPreferences.value = await saveNotificationPreferences({ [key]: checked });
    setStatus('Notification preference saved.', 'success', 3000);
  } catch (error) {
    notificationPreferences.value = { ...notificationPreferences.value, [key]: previous };
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isSavingNotifications.value = false;
  }
}

async function enablePush(): Promise<void> {
  isChangingPush.value = true;
  try {
    const result = await enableNotificationPush();
    pushPermission.value = getPushPermissionState();
    pushEnabled.value = result === 'enabled';
    setStatus(result === 'enabled' ? 'Browser notifications enabled.' : result === 'denied'
      ? 'Notification permission was not granted.' : 'This browser does not support notifications.', result === 'enabled' ? 'success' : 'neutral', 5000);
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isChangingPush.value = false;
  }
}

async function disablePush(): Promise<void> {
  isChangingPush.value = true;
  try {
    await disableNotificationPush();
    pushPermission.value = getPushPermissionState();
    pushEnabled.value = false;
    setStatus('Browser notifications disabled for this device.', 'success', 5000);
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isChangingPush.value = false;
  }
}

function handleUserProfileUpdated(event: Event): void {
  currentUser.value = (event as CustomEvent<CachedUser>).detail;
  isProfileReady.value = true;
}

watch(themeSelection, (mode) => {
  if (mode !== getThemeMode()) {
    setThemeMode(mode);
  }
});

watch(defaultListLengthSelection, (value) => {
  void saveDefaultListLength(value);
});

function normalizeListLength(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
  return VALID_LIST_LENGTHS.includes(parsed as typeof VALID_LIST_LENGTHS[number]) ? parsed : null;
}

function resolveDefaultListLength(): number {
  const remotePreference = normalizeListLength(currentUser.value?.preferences?.defaultListLength);
  if (remotePreference) {
    return remotePreference;
  }

  try {
    return normalizeListLength(localStorage.getItem('shipments_pageLength')) || DEFAULT_LIST_LENGTH;
  } catch {
    return DEFAULT_LIST_LENGTH;
  }
}

async function saveDefaultListLength(value: string): Promise<void> {
  const listLength = normalizeListLength(value);
  const cachedUser = currentUser.value;

  if (!listLength || !cachedUser?.uid) {
    return;
  }

  isSavingListLength.value = true;

  try {
    const updatedUser = await updateCurrentUserPreferences({ defaultListLength: listLength });

    currentUser.value = updatedUser;
    localStorage.setItem('USER_KEY', JSON.stringify(updatedUser));
    localStorage.setItem('shipments_pageLength', String(listLength));
    setStatus('Default list length saved.', 'success', 5000);
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isSavingListLength.value = false;
  }
}

function setStatus(message: string, tone: 'success' | 'danger' | 'neutral' = 'neutral', durationMs = 0): void {
  if (statusTimeout) {
    clearTimeout(statusTimeout);
    statusTimeout = null;
  }

  statusMessage.value = message;
  statusTone.value = tone;

  if (durationMs > 0) {
    statusTimeout = setTimeout(() => {
      statusMessage.value = '';
      statusTimeout = null;
    }, durationMs);
  }
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Something went wrong.';
}

function parsePositiveInteger(value: string, fallback: number): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

async function refreshProfile(showSuccess: boolean): Promise<void> {
  isRefreshingProfile.value = true;
  isProfileReady.value = false;

  try {
    await refreshCurrentUserProfile();
    currentUser.value = getCurrentUser();
    isProfileReady.value = true;
    const remoteListLength = normalizeListLength(currentUser.value?.preferences?.defaultListLength);
    if (remoteListLength) {
      defaultListLengthSelection.value = String(remoteListLength);
    }
    if (showSuccess) {
      setStatus('Profile refreshed.', 'success', 5000);
    }
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isRefreshingProfile.value = false;
  }
}

async function loadApiKeys(): Promise<void> {
  if (!hasApiAccess.value) {
    return;
  }

  isLoadingKeys.value = true;

  try {
    const result = await listApiKeysCallable(undefined);
    apiKeys.value = result.data.data.apiKeys;
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isLoadingKeys.value = false;
  }
}

async function createKey(): Promise<void> {
  if (!hasApiAccess.value || !newKeyName.value.trim()) {
    return;
  }

  isCreatingKey.value = true;
  newToken.value = '';

  try {
    const result = await createApiKeyCallable({
      name: newKeyName.value.trim(),
      rateLimit: {
        enabled: true,
        perMinute: parsePositiveInteger(rateLimitPerMinute.value, 60),
        perDay: parsePositiveInteger(rateLimitPerDay.value, 1000),
        burst: 20
      }
    });

    newToken.value = result.data.data.token;
    setStatus('API key created. Copy the token before leaving this page.', 'success');
    await loadApiKeys();
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    isCreatingKey.value = false;
  }
}

async function rotateKey(keyId: string): Promise<void> {
  if (!window.confirm('Rotate this API key? Existing integrations using the old token will stop working.')) {
    return;
  }

  actionKeyId.value = keyId;
  newToken.value = '';

  try {
    const result = await rotateApiKeyCallable({ keyId });
    newToken.value = result.data.data.token;
    setStatus('API key rotated. Copy the new token before leaving this page.', 'success');
    await loadApiKeys();
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    actionKeyId.value = null;
  }
}

async function deleteKey(keyId: string): Promise<void> {
  if (!window.confirm('Revoke this API key? This keeps audit history but blocks future use.')) {
    return;
  }

  actionKeyId.value = keyId;

  try {
    await deleteApiKeyCallable({ keyId });
    setStatus('API key revoked.', 'success');
    await loadApiKeys();
  } catch (error) {
    setStatus(getErrorMessage(error), 'danger');
  } finally {
    actionKeyId.value = null;
  }
}

async function copyToken(): Promise<void> {
  if (!newToken.value) {
    return;
  }

  try {
    await navigator.clipboard.writeText(newToken.value);
    setStatus('Token copied.', 'success');
  } catch {
    setStatus('Copy failed. Select the token and copy it manually.', 'danger');
  }
}

function formatDate(value: unknown): string {
  if (!value) {
    return 'Never';
  }

  let date: Date | null = null;

  if (typeof value === 'object' && value !== null && 'seconds' in value && typeof (value as { seconds?: unknown }).seconds === 'number') {
    date = new Date((value as { seconds: number }).seconds * 1000);
  } else if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    date = (value as { toDate: () => Date }).toDate();
  } else if (typeof value === 'string' || typeof value === 'number') {
    date = new Date(value);
  }

  if (!date || Number.isNaN(date.getTime())) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
}

onMounted(async () => {
  window.addEventListener(USER_PROFILE_UPDATED_EVENT, handleUserProfileUpdated);
  unsubscribeTheme = subscribeTheme(({ mode }) => {
    themeSelection.value = mode;
  });

  await refreshProfile(false);

  if (currentUser.value?.uid) {
    unsubscribeNotificationPreferences = subscribeNotificationPreferences(currentUser.value.uid, preferences => {
      notificationPreferences.value = preferences;
    }, error => setStatus(getErrorMessage(error), 'danger'));
  }

  await loadApiKeys();
});

onBeforeUnmount(() => {
  window.removeEventListener(USER_PROFILE_UPDATED_EVENT, handleUserProfileUpdated);
  if (statusTimeout) {
    clearTimeout(statusTimeout);
  }
  unsubscribeTheme?.();
  unsubscribeNotificationPreferences?.();
});
</script>
